// shell/Rail/AccountChip.tsx — the rail's bottom account slot.
//
// Signed out → a "Sign in" link (→ /signin). Signed in → avatar + name/@handle,
// with the consolidated, customizable account menu. Reads session and preferences.
//
// `collapsed` (icons-only rail): the signed-out link and the signed-in trigger render avatar/icon
// only — no wordmark or handle — so they fit the 76px collapsed column.
//
// PAID MARKS: exactly one, and it is `currentUser.supporterBadge` (server-derived:
// has_pro AND confirmed 2FA). The raw `currentUser.premium` mirror is never rendered
// — an ungated "Premium" pill used to sit here and gave a visible paid mark to the
// very accounts the 2FA gate withholds the badge from. See AccountChip.test.tsx.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Popover } from '@/ui';
import { Icon } from '@/icons';
import { useSession } from '@/hooks';
import { usePreferences } from '@/hooks/usePreferences';
import {
  ACCOUNT_MENU_LABELS,
  normalizeAccountMenu,
  type AccountMenuShortcut,
} from '@/core/models/accountMenu';
import type { IconName } from '@/icons';
import { softControlMotion } from '@/theme/interactions';

const SHORTCUTS: Record<AccountMenuShortcut, { path: string; icon: IconName }> = {
  'edit-profile': { path: '/settings/profile', icon: 'edit' },
  appearance: { path: '/settings/appearance', icon: 'settings' },
  'invite-friends': { path: '/settings/account#invite', icon: 'user-plus' },
};

const SignInLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-decoration: none;
  /* Secondary: a signed-out visitor already meets a filled call to action in
     the persistent bottom bar and another in the topbar. Three accent fills on
     one screen is the scarcity that makes an accent mean something, spent. */
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
`;

const SignInIcon = styled.a`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin: 0 auto;
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
`;

const Trigger = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: ${({ $collapsed }) => ($collapsed ? '5px' : '10px')};
  border-radius: 18px;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.color.hover};
  }
  > [role='img'] {
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

const AccountPopover = styled(Popover)<{ $collapsed: boolean }>`
  ${softControlMotion}
  display: block;
  width: 100%;
  > [role='menu'] {
    width: ${({ $collapsed }) => ($collapsed ? '220px' : '100%')};
    min-width: 0;
    border-radius: 18px;
    max-height: calc(100dvh - 150px);
    overflow-y: auto;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Supporter mark. Its own colour token, NOT an accent token — `accentAmber`
   resolves to the same purple as `accentCyan` (the verified check's colour),
   which made the two marks colour-identical. See theme/tokens.ts. */
const SupporterGlyph = styled.span`
  margin-left: 4px;
  display: inline-flex;
  vertical-align: middle;
  color: ${({ theme }) => theme.color.supporter};
`;

const MenuItem = styled.button`
  ${softControlMotion}
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 12px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
`;

export function AccountChip({ collapsed = false }: { collapsed?: boolean }) {
  const { prefs } = usePreferences();
  const { currentUser, isAuthenticated, signOut } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open]);

  function visit(path: string) {
    setOpen(false);
    navigate(path);
  }

  if (!isAuthenticated || !currentUser) {
    if (collapsed) {
      return (
        <SignInIcon
          href="/signin"
          aria-label="Sign in"
          onClick={(e) => {
            e.preventDefault();
            navigate('/signin');
          }}
        >
          <Icon name="user" size={18} />
        </SignInIcon>
      );
    }
    return (
      <SignInLink
        href="/signin"
        onClick={(e) => {
          e.preventDefault();
          navigate('/signin');
        }}
      >
        <Icon name="user" size={16} />
        Sign in
      </SignInLink>
    );
  }

  return (
    <AccountPopover
      $collapsed={collapsed}
      align="start"
      placement="top"
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Trigger $collapsed={collapsed} aria-label="Account menu">
          <Avatar
            name={currentUser.name}
            size={collapsed ? 36 : 34}
            avatarKey={currentUser.avatarKey}
          />
          {!collapsed && (
            <>
              <Meta>
                <Name>
                  {currentUser.name}
                  {currentUser.supporterBadge && (
                    <SupporterGlyph>
                      <Icon name="supporter-badge" size={13} title="Supporter" />
                    </SupporterGlyph>
                  )}
                </Name>
                <Handle>{currentUser.handle}</Handle>
              </Meta>
            </>
          )}
        </Trigger>
      }
    >
      <div
        ref={menuRef}
        onKeyDown={(event) => {
          const buttons = Array.from(
            event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
          );
          const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next =
            event.key === 'ArrowDown'
              ? (index + 1) % buttons.length
              : event.key === 'ArrowUp'
                ? (index - 1 + buttons.length) % buttons.length
                : event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? buttons.length - 1
                    : -1;
          if (next >= 0) {
            event.preventDefault();
            buttons[next]?.focus();
          }
        }}
      >
        <MenuItem role="menuitem" onClick={() => visit('/profile')}>
          <Icon name="user" size={16} /> Visit Profile
        </MenuItem>
        {normalizeAccountMenu(prefs.accountMenuShortcuts).map((key) => (
          <MenuItem key={key} role="menuitem" onClick={() => visit(SHORTCUTS[key].path)}>
            <Icon name={SHORTCUTS[key].icon} size={16} /> {ACCOUNT_MENU_LABELS[key]}
          </MenuItem>
        ))}
        <MenuItem role="menuitem" onClick={() => visit('/settings')}>
          <Icon name="settings" size={16} /> Settings
        </MenuItem>
        <MenuItem
          role="menuitem"
          onClick={() => {
            setOpen(false);
            signOut();
            navigate('/');
          }}
        >
          <Icon name="logout" size={16} /> Sign out
        </MenuItem>
      </div>
    </AccountPopover>
  );
}
